import type {
	MyPortfolioDashboardCompletionQuery,
	MyPortfolioWithMediaQuery,
} from "src/__generated__/graphql";

export type LaborPassCompletionState =
	| "incomplete"
	| "required-complete"
	| "fully-complete";

export type LaborPassEditSource =
	| "project-history"
	| "employment-history"
	| "certification"
	| "education";

export interface LaborPassEditSuccessSignal {
	source: LaborPassEditSource;
	id: string;
}

export type LaborPassCompletionPortfolio =
	| NonNullable<MyPortfolioDashboardCompletionQuery["myPortfolio"]>
	| NonNullable<MyPortfolioWithMediaQuery["myPortfolio"]>;

export const LABORPASS_PROFILE_GENERATION_PROMPT_SESSION_KEY =
	"laborpass-profile-generation-prompt-shown";

const hasText = (value?: string | null) =>
	typeof value === "string" && value.trim().length > 0;

const getItemCount = (items?: Array<unknown> | null) =>
	Array.isArray(items) ? items.length : 0;

export const hasLaborPassWorkHistory = (
	portfolio?: LaborPassCompletionPortfolio | null
) =>
	getItemCount(portfolio?.workHistory) > 0 ||
	getItemCount(portfolio?.employmentHistory) > 0;

export const getLaborPassCompletionState = (
	portfolio?: LaborPassCompletionPortfolio | null
): LaborPassCompletionState => {
	const hasOverview = hasText(portfolio?.overview);
	const hasWorkHistory = hasLaborPassWorkHistory(portfolio);
	const hasCertification = getItemCount(portfolio?.certifications) > 0;
	const hasEducation = getItemCount(portfolio?.educationEntries) > 0;

	if (!hasOverview || !hasWorkHistory) {
		return "incomplete";
	}

	if (hasCertification && hasEducation) {
		return "fully-complete";
	}

	return "required-complete";
};

/**
 * Returns a prompt message indicating which sections the Pro should complete next on their LaborPass to strengthen it from required-complete state, or null if no additional sections need to be completed.
 * @param portfolio
 * @returns
 */
export const getLaborPassRemainingCompletionPrompt = (
	portfolio?: LaborPassCompletionPortfolio | null
) => {
	const remainingSections: string[] = [];

	// Get the sections that are not yet completed
	if (getItemCount(portfolio?.certifications) === 0) {
		remainingSections.push("certifications");
	}

	if (getItemCount(portfolio?.educationEntries) === 0) {
		remainingSections.push("education");
	}

	if (remainingSections.length === 0) {
		return null;
	}

	// Determine the appropriate label based on what sections are remaining
	const remainingSectionsLabel =
		remainingSections.length === 1
			? remainingSections[0]
			: `${remainingSections.slice(0, -1).join(", ")} and ${remainingSections.at(-1)}`;

	return `Add ${remainingSectionsLabel} next to strengthen your LaborPass further.`;
};

export const getPublicLaborPassUrl = (handle: string) => {
	const baseUrl =
		process.env.REACT_APP_HOME_APP_URL?.replace(/\/$/, "") ||
		window.location.origin;

	return `${baseUrl}/pros/@${handle}`;
};

export const buildWhatsAppLaborPassShareTarget = (publicUrl: string) => {
	const text = `Take a look at my LaborPass on LaborHack: ${publicUrl}`;

	return `https://wa.me/?text=${encodeURIComponent(text)}`;
};

export const buildLinkedInLaborPassCopy = (publicUrl: string) =>
	`LaborPass portfolio: ${publicUrl}`;

export const buildDirectLaborPassLinkCopy = (publicUrl: string) => publicUrl;

export const createLaborPassEditSuccessState = (
	source: LaborPassEditSource
) => ({
	laborPassEditSuccess: {
		source,
		id: `${source}-${Date.now()}`,
	},
});

export const getLaborPassEditSuccessSignalFromLocationState = (
	state: unknown
): LaborPassEditSuccessSignal | null => {
	if (!state || typeof state !== "object") {
		return null;
	}

	const signal = (
		state as { laborPassEditSuccess?: LaborPassEditSuccessSignal }
	).laborPassEditSuccess;

	if (!signal?.source || !signal?.id) {
		return null;
	}

	return signal;
};

interface ShouldPromptForLaborPassProfileGenerationArgs {
	hasSeenPromptThisSession: boolean;
	portfolio?: LaborPassCompletionPortfolio | null;
	successSignal?: LaborPassEditSuccessSignal | null;
}

export const shouldPromptForLaborPassProfileGeneration = ({
	hasSeenPromptThisSession,
	portfolio,
	successSignal,
}: ShouldPromptForLaborPassProfileGenerationArgs) => {
	if (hasSeenPromptThisSession || !successSignal) {
		return false;
	}

	return !hasText(portfolio?.overview) && hasLaborPassWorkHistory(portfolio);
};
